import { Link } from "wouter";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import PageHero from "@/components/PageHero";
import { CONTACT } from "@/lib/constants";

export default function Villkor() {
  return (
    <Layout>
      <SEOHead
        title="Allmänna villkor | ASPAB"
        description="Läs ASPAB:s allmänna villkor för tjänster inom laserskärning, kantpressning och svetsning. Villkor för offerter, leveranser, betalning och reklamationer."
        canonical="https://aspab.com/villkor"
        path="/villkor"
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Allmänna villkor", href: "/villkor" },
        ]}

        ogImage="/images/og-villkor_cf9c230e.png"
      />

      <PageHero
        title="Allmänna villkor"
        subtitle="Villkor för våra tjänster inom laserskärning, kantpressning och svetsning."
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Allmänna villkor", href: "/villkor" },
        ]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <p className="text-gray-500 text-sm mb-10">
              Senast uppdaterad: 20 mars 2025
            </p>

            <div className="space-y-12">

              {/* 1. Allmänt */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  1. Allmänt
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Dessa allmänna villkor gäller för alla tjänster som tillhandahålls av ASPAB
                  (org.nr {CONTACT.orgNr}), med säte i Eskilstuna. Genom att anlita ASPAB för
                  uppdrag inom laserskärning, kantpressning, svetsning, avgradning eller
                  relaterade tjänster accepterar kunden dessa villkor i sin helhet, om inte
                  annat skriftligen avtalats.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Vid eventuell motstridighet mellan dessa allmänna villkor och ett separat
                  skriftligt avtal mellan parterna ska det skriftliga avtalet ha företräde.
                </p>
              </div>

              {/* 2. Offerter och beställningar */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  2. Offerter och beställningar
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Offerter från ASPAB är giltiga i 30 dagar från offertdatum, om inte annat
                  anges i offerten. Priser i offerter är exklusive mervärdesskatt (moms) om
                  inte annat uttryckligen anges.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  En beställning anses bindande när kunden skriftligen (via e-post, brev eller
                  digitalt formulär) bekräftar offerten eller lämnar en beställning. ASPAB
                  förbehåller sig rätten att justera priser vid väsentliga förändringar i
                  materialpriser, valutakurser eller andra omständigheter utanför ASPAB:s kontroll.
                </p>
              </div>

              {/* 3. Underlag och ritningar */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  3. Underlag och ritningar
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Kunden ansvarar för att leverera korrekta och fullständiga underlag,
                  ritningar och specifikationer. ASPAB tar emot CAD-filer (DXF, DWG, STEP,
                  IGES), PDF-ritningar och andra vanliga filformat. Vi granskar alltid
                  inkomna underlag och meddelar kunden om vi upptäcker oklarheter eller
                  potentiella problem.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  ASPAB ansvarar inte för fel som uppstår till följd av felaktiga eller
                  ofullständiga underlag från kunden, såvida felet borde ha upptäckts vid
                  en rimlig granskning.
                </p>
              </div>

              {/* 4. Leveranser */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  4. Leveranser
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Leveranstider anges som uppskattningar baserade på aktuell beläggning och
                  materialets tillgänglighet. ASPAB strävar alltid efter att hålla utlovade
                  leveranstider och meddelar kunden omgående vid eventuella förseningar.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Leverans sker i enlighet med överenskommelse – antingen genom kundens
                  egen hämtning på vår anläggning i Eskilstuna, eller via transport arrangerad
                  av ASPAB eller kunden. Fraktkostnader tillkommer enligt överenskommelse.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Risken för godset övergår till kunden vid leveranstillfället, det vill säga
                  när godset hämtas av kunden eller överlämnas till transportör.
                </p>
              </div>

              {/* 5. Betalningsvillkor */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  5. Betalningsvillkor
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Betalning ska ske inom 30 dagar från fakturadatum, om inte annat avtalats.
                  Vid försenad betalning debiteras dröjsmålsränta enligt räntelagen. ASPAB
                  förbehåller sig rätten att kräva förskottsbetalning eller säkerhet för
                  nya kunder eller vid beställningar av större värde.
                </p>
              </div>

              {/* 6. Kvalitet och toleranser */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  6. Kvalitet och toleranser
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  ASPAB garanterar att alla levererade produkter uppfyller de toleranser och
                  kvalitetskrav som anges i offerten eller beställningen. Vid laserskärning
                  arbetar vi med toleranser ner till 0,1 mm. Varje nytt uppdrag inleds med
                  en första bit-kontroll för att säkerställa att specifikationerna uppfylls
                  innan serieproduktion påbörjas.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Om inga specifika toleranser anges i beställningen tillämpar ASPAB
                  branschstandard för respektive bearbetningsmetod.
                </p>
              </div>

              {/* 7. Reklamationer */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  7. Reklamationer
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Kunden ska kontrollera levererade produkter vid mottagande. Eventuella
                  reklamationer ska meddelas ASPAB skriftligen inom 14 dagar från
                  leveransdatum. Reklamationen ska innehålla en tydlig beskrivning av felet
                  samt, om möjligt, fotografier.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Vid godkänd reklamation åtgärdar ASPAB felet genom ombearbetning,
                  nytillverkning eller kreditering, efter överenskommelse med kunden.
                  ASPAB:s ansvar begränsas till värdet av den reklamerade produkten.
                </p>
              </div>

              {/* 8. Ansvarsbegränsning */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  8. Ansvarsbegränsning
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  ASPAB:s totala ansvar för skador, oavsett grund, är begränsat till det
                  fakturerade beloppet för det aktuella uppdraget. ASPAB ansvarar inte för
                  indirekta skador, följdskador, utebliven vinst eller produktionsbortfall.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  ASPAB ansvarar inte för förseningar eller brister som beror på
                  omständigheter utanför ASPAB:s kontroll, inklusive men inte begränsat till
                  force majeure, materialbrist, transportstörningar eller myndighetsåtgärder.
                </p>
              </div>

              {/* 9. Immateriella rättigheter */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  9. Immateriella rättigheter
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Kunden behåller alla immateriella rättigheter till de ritningar, modeller
                  och specifikationer som lämnas till ASPAB. ASPAB förbinder sig att inte
                  använda kundens underlag för andra ändamål än att utföra det beställda
                  uppdraget, och att hantera alla underlag konfidentiellt.
                </p>
              </div>

              {/* 10. Sekretess */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  10. Sekretess
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  ASPAB behandlar all information som mottas från kunden som konfidentiell,
                  inklusive ritningar, specifikationer, volymer och affärsvillkor. Denna
                  sekretess gäller även efter att uppdraget avslutats. Undantag gäller för
                  information som är allmänt känd eller som ASPAB är skyldig att lämna ut
                  enligt lag.
                </p>
              </div>

              {/* 11. Webbplats och digitalt innehåll */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  11. Webbplats och digitalt innehåll
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Allt innehåll på aspab.com – inklusive text, bilder, grafik, logotyper och
                  teknisk information – tillhör ASPAB eller dess licensgivare och skyddas av
                  upphovsrätt. Innehållet får inte kopieras, distribueras eller användas i
                  kommersiellt syfte utan skriftligt medgivande från ASPAB.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  ASPAB strävar efter att all information på webbplatsen ska vara korrekt och
                  aktuell, men garanterar inte att innehållet är fritt från fel. Tekniska
                  specifikationer och priser kan ändras utan föregående meddelande.
                </p>
              </div>

              {/* 12. Personuppgifter */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  12. Personuppgifter
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  ASPAB behandlar personuppgifter i enlighet med EU:s dataskyddsförordning
                  (GDPR). För fullständig information om hur vi hanterar personuppgifter,
                  se vår{" "}
                  <Link href="/integritetspolicy" className="text-[#2563EB] hover:underline">
                    integritetspolicy
                  </Link>.
                </p>
              </div>

              {/* 13. Tvister */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  13. Tvister och tillämplig lag
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Dessa villkor regleras av svensk lag. Eventuella tvister som uppstår i
                  anslutning till dessa villkor ska i första hand lösas genom förhandling
                  mellan parterna. Om parterna inte kan nå en överenskommelse ska tvisten
                  avgöras av Eskilstuna tingsrätt.
                </p>
              </div>

              {/* 14. Kontakt */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  14. Kontaktuppgifter
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Vid frågor om dessa villkor, kontakta oss:
                </p>
                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <div className="text-gray-600 text-sm space-y-1">
                    <p className="font-medium text-gray-700">ASPAB</p>
                    <p>{CONTACT.address}, {CONTACT.city}</p>
                    <p>E-post: <a href={`mailto:${CONTACT.email}`} className="text-[#2563EB] hover:underline">{CONTACT.email}</a></p>
                    <p>Telefon: <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`} className="text-[#2563EB] hover:underline">{CONTACT.phone}</a></p>
                    <p>Org.nr: {CONTACT.orgNr}</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
